import React, { useState } from 'react';
import {
  Pencil, Check, X, Info, AlertTriangle, CheckCircle, Search, PlusCircle, Trash2,
  ClipboardCopy, Bot, FileText, Loader2, AlertCircle
} from 'lucide-react'; // Import all potentially needed icons

// Komponent do renderowania komórki z nazwą pliku (z możliwością edycji)
const AdminFileNameCell = ({ fileObj, onSave, isChild, showPopup }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(fileObj.fileName);

  const handleSave = () => {
    let finalName = tempName.trim();
    if (!finalName.toLowerCase().endsWith('.pdf')) {
      finalName += '.pdf';
    }
    
    if (finalName === fileObj.fileName) {
      setIsEditing(false);
      return;
    }

    onSave(fileObj.id, finalName);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempName(fileObj.fileName);
    setIsEditing(false);
  };

  const handleStartEdit = () => {
    setTempName(fileObj.fileName);
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <input
          type="text"
          value={tempName}
          onChange={(e) => setTempName(e.target.value)}
          className="admin-filename-input"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
          style={{ padding: '4px 8px', border: '1px solid #ccc', borderRadius: '4px' }}
        />
        <button onClick={handleSave} className="admin-btn-icon" title="Zapisz">
          <Check size={16} />
        </button>
        <button onClick={handleCancel} className="admin-btn-icon" title="Anuluj">
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <>
      {fileObj.fileName}
      {isChild && (
        <span className="file-tag file-tag-child" style={{ marginLeft: '6px' }}>
          zależny
        </span>
      )}
      <button
        onClick={handleStartEdit}
        className="admin-btn-icon"
        style={{ marginLeft: '8px', verticalAlign: 'middle' }}
        title="Edytuj nazwę pliku"
      >
        <Pencil size={14} />
      </button>
    </>
  );
};


// Komponent wewnętrzny do edycji listy gości
const GuestListEditor = ({ fileId, guestsText, isLoading, onTextChange, onSave }) => {
  return (
    <tr className="guest-list-row">
      <td colSpan={4}> {/* Adjusted colspan */}
        {isLoading ? (
          <p className="loading-text">Wczytywanie / zapisywanie...</p>
        ) : (
          <div className="guest-list-editor">
            <textarea
              rows={6}
              value={guestsText}
              onChange={(e) => onTextChange(fileId, e.target.value)}
              className="guest-list-textarea"
            />
            <button
              onClick={() => onSave(fileId)} // Pass only fileId, fileObj is known in parent
              className="admin-btn admin-btn-save"
            >
              Zapisz listę gości
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

export default function OrderFilesTable({
  files, // Array of file objects
  statuses, // Array of status objects
  editedQuantities, // Map { fileId: quantity }
  guestsOpenMap, // Map { fileId: boolean }
  guestsTextMap, // Map { fileId: string }
  guestsLoadingMap, // Map { fileId: boolean }
  aiStateMap, // Map { projectId: { status, message, interactionId } } <- Still used for interactionId and error message
  duplicatingFileId, // ID of file being duplicated
  deletingFileId, // ID of file being deleted
  actions, // Object containing action handlers from useOrderDetailsActions
  uiActions, // Object containing UI state handlers from useOrderDetailsUIState
  dataActions // Object containing data state setters/handlers from useOrderDetailsData
}) {

  const normalFiles = files.filter((f) => !f._prod); // Filter non-production files

  if (normalFiles.length === 0) {
    return <p style={{ fontSize: "1.1rem", marginTop: 15 }}>Brak plików.</p>;
  }

  // Helper to get status name
  const getStatusName = (statusId) => {
    const status = statuses.find(s => s.id === statusId);
    return status ? status.name : '(Brak statusu)';
  };

  return (
    <table
      className="files-table"
      style={{ marginTop: 15, fontSize: "1.05rem", width: "100%" }}
    >
      <thead>
        <tr>
          <th style={{ padding: "8px", textAlign: 'left' }}>Nazwa pliku</th>
          <th style={{ padding: "8px", textAlign: 'left', width: '80px' }}>Ilość</th>
          <th style={{ padding: "8px", textAlign: 'left' }}>Akcje</th>
          <th style={{ padding: "8px", minWidth: "120px", textAlign: 'left' }}>Status</th>
        </tr>
      </thead>
      <tbody>
        {normalFiles.map((fileObj) => {
          const isPdf = fileObj.fileName.toLowerCase().endsWith(".pdf");
          const isGuestListOpen = guestsOpenMap[fileObj.id];
          const guestsTextValue = guestsTextMap[fileObj.id] || "";
          const isLoadingGuests = guestsLoadingMap[fileObj.id] || false;
          const localAiState = aiStateMap[fileObj.id] || {}; // Get local state for interactionId/error message
          const backendAiStatus = fileObj.aiProcessingStatus; // Get status from backend data

          // Determine UI states based on BOTH local and backend states
          // Show spinner if EITHER local state is 'loading' OR backend state is 'processing'
          const isLoadingAi = localAiState.status === 'loading' || backendAiStatus === 'processing'; // Zmieniono 'pending' na 'processing'
          // Show success/error based on backend status, but only if not currently loading
          const isAiSuccess = !isLoadingAi && backendAiStatus === 'completed';
          const isAiError = !isLoadingAi && backendAiStatus === 'error';
          // Get interactionId/message from local state (set on completion/error by handleAiRequestSubmit)
          const interactionId = localAiState.interactionId;
          const errorMessage = localAiState.message;

          const isChild = !!fileObj.parentFileId;
          const canGeneratePersonalized = isPdf && !isChild; // Can generate personalized if PDF and not a child

          return (
            <React.Fragment key={fileObj.id}>
              <tr>
                <td style={{ padding: "8px", verticalAlign: 'top' }}>
                  <AdminFileNameCell
                    fileObj={fileObj}
                    onSave={actions.handleSaveFileName}
                    isChild={isChild}
                    showPopup={uiActions.showPopup}
                  />
                </td>
                {/* Quantity Column */}
                <td style={{ padding: "8px", verticalAlign: 'top' }}>
                  {isChild ? ( // If it's a child project, always display read-only quantity
                    <div className="quantity-display quantity-display-auto" title="Ilość ustalana automatycznie na podstawie listy gości">
                      {fileObj.quantity ?? '-'}
                      {/* Optional: Keep label only if it also has personalization */}
                      {fileObj.hasPersonalization && <span className="quantity-auto-label">(lista gości)</span>}
                    </div>
                  ) : ( // If it's a parent project, always show the input field
                    <input
                      type="number"
                      min="0" // Allow 0 for parent projects if needed
                      value={editedQuantities[fileObj.id] ?? fileObj.quantity ?? ""} // Default to current quantity if not edited
                      onChange={(e) => dataActions.setEditedQuantities(prev => ({ ...prev, [fileObj.id]: e.target.value }))}
                      onBlur={() => actions.handleSaveFileChanges(fileObj.id)} // Save on blur
                      className="quantity-input"
                      style={{ width: '60px' }}
                      // Input is never disabled here because we are in the !isChild branch
                    />
                  )}
                </td>
                {/* Actions Column */}
                <td style={{ padding: "8px", verticalAlign: 'top' }}>
                  <a
                    href={`${actions.backendUrl}/admin/files/${fileObj.downloadKey}`} // Assuming backendUrl is accessible via actions or passed separately
                    download={fileObj.fileName}
                    className="action-button action-link"
                  >
                    Pobierz
                  </a>

                  {isPdf && (
                    <div className="action-buttons-group">
                      <button onClick={() => actions.handleShowMetadata(fileObj.downloadKey)} className="admin-btn-outlined">Metadane</button>
                      <button onClick={() => actions.handleNavigateToEditor(fileObj.downloadKey)} className="admin-btn">Edytor</button>
                      <button onClick={() => actions.handleGeneratePdf(fileObj.downloadKey)} className="admin-btn-outlined">Generuj PDF</button>
                      <button onClick={() => actions.handlePreviewPdf(fileObj.downloadKey)} className="admin-btn-outlined">Podgląd PDF</button>

                      {!isChild && (
                        <button
                          onClick={() => {
                            uiActions.handleToggleGuests(fileObj.id);
                            if (!isGuestListOpen) {
                              actions.handleFetchGuestsForFile(fileObj); // Pass fileObj here
                            }
                          }}
                          className="admin-btn-outlined"
                        >
                          {isGuestListOpen ? "Ukryj gości" : "Lista gości"}
                        </button>
                      )}

                      {canGeneratePersonalized && (
                        <button onClick={() => actions.handleGeneratePersonalizedProject(fileObj)} className="admin-btn admin-btn-generate">
                          Generuj z listą gości
                        </button>
                      )}

                      {!isChild && (
                        <button
                          className="admin-btn admin-btn-duplicate"
                          onClick={() => actions.handleDuplicateProject(fileObj)}
                          disabled={duplicatingFileId === fileObj.id}
                        >
                          {duplicatingFileId === fileObj.id ? "Duplikowanie..." : "Duplikuj"}
                        </button>
                      )}

                      {/* AI Buttons */}
                      {!isChild && isPdf && (
                        <>
                          <button
                            className="admin-btn admin-btn-ai"
                            onClick={() => uiActions.handleOpenAiModal(fileObj.id)}
                            disabled={isLoadingAi} // Disable based on backend status 'pending'
                            title="Generuj modyfikacje za pomocą AI"
                          >
                            <Bot size={14} style={{ marginRight: '4px' }} />
                            Generuj AI
                          </button>
                          {/* Show logs button if interactionId exists (from local state after completion/error) */}
                          {interactionId && (
                            <button
                              className="admin-btn-icon admin-btn-icon-logs"
                              onClick={() => {
                                uiActions.handleShowLogs(fileObj.id, interactionId); // Set loading state and show modal structure
                                actions.handleFetchAiLogs(fileObj.id, interactionId); // Fetch the actual logs
                              }}
                              title="Pokaż logi ostatniej interakcji AI"
                            >
                              <FileText size={14} />
                            </button>
                          )}
                          {/* AI Status Indicator based on combined loading state and backend success/error */}
                          <span className="ai-status-indicator" style={{ marginLeft: '5px' }}>
                            {isLoadingAi && <Loader2 size={16} className="animate-spin" title="Przetwarzanie AI..." />}
                            {isAiSuccess && <CheckCircle size={16} color="green" title="Przetwarzanie AI zakończone."/>}
                            {isAiError && <AlertCircle size={16} color="red" title={`Błąd przetwarzania AI: ${errorMessage || '(brak szczegółów)'}`} />}
                          </span>
                        </>
                      )}

                      <button
                        className="admin-btn-icon admin-btn-icon-delete"
                        onClick={() => actions.handleDeleteProject(fileObj)}
                        disabled={deletingFileId === fileObj.id || isLoadingAi} // Disable based on backend status 'pending'
                        title="Usuń projekt"
                      >
                        {deletingFileId === fileObj.id ? (
                          <div className="popup-spinner popup-spinner-button-small"></div>
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  )}
                </td>
                {/* Status Column */}
                <td style={{ padding: "8px", verticalAlign: 'top' }}>
                  <select
                    value={fileObj.statusId || ""}
                    onChange={(e) => actions.handleStatusChange(fileObj.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="">(Brak statusu)</option>
                    {statuses.map((st) => (
                      <option key={st.id} value={st.id}>
                        {st.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>

              {/* Guest List Editor Row */}
              {isGuestListOpen && isPdf && !isChild && (
                <GuestListEditor
                  fileId={fileObj.id}
                  guestsText={guestsTextValue}
                  isLoading={isLoadingGuests}
                  onTextChange={(id, text) => uiActions.setGuestsTextMap(prev => ({ ...prev, [id]: text }))}
                  onSave={() => actions.handleSaveGuestsForFile(fileObj)} // Pass fileObj here
                />
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );
}
